import { useDemoLock } from '@/hooks/demo-lock';
import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  fullWidth?: boolean;
  /**
   * Le bouton écrit-il ? Sert au verrou de démonstration. Par défaut on le
   * déduit : un `submit` valide un formulaire et un `danger` supprime, donc les
   * deux écrivent ; le reste navigue, ouvre ou filtre. À renseigner quand la
   * déduction se trompe — un `primary` qui enregistre, par exemple.
   */
  writes?: boolean;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    'bg-[#293DED] hover:bg-[#1124CF] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_1px_0_rgba(0,0,0,0.55)] active:shadow-[inset_0_1px_3px_rgba(0,0,0,0.45)] active:translate-y-[0.5px]',
  secondary:
    'bg-[#131318] hover:bg-[#1f1f28] text-white border border-white/[0.06] shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_0_rgba(0,0,0,0.45)] active:shadow-[inset_0_1px_3px_rgba(0,0,0,0.35)] active:translate-y-[0.5px]',
  ghost:
    'bg-transparent hover:bg-white/[0.05] text-neutral-300',
  danger:
    'bg-[rgba(248,113,113,0.10)] hover:bg-[rgba(248,113,113,0.18)] text-[#f87171] border border-[rgba(248,113,113,0.25)] shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_1px_0_rgba(0,0,0,0.40)]',
};

/// Deux densités. Celles de droite — 28, 32, 36px — se pointent à la souris au
/// pixel près ; au pouce, dont la surface de contact fait environ 9mm, elles se
/// ratent. Le plancher mobile vaut aussi pour le texte : 11px se lit assis
/// devant un écran, moins bien en marchant.
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'h-9 text-[12px] px-3 md:h-7 md:text-[11px] md:px-2.5',
  md: 'h-10 text-[13px] px-3.5 md:h-8 md:text-[12px] md:px-3',
  lg: 'h-11 text-[14px] px-4 md:h-9 md:text-[13px]',
};

const SPINNER_SIZE: Record<ButtonSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
};

function Spinner({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className="animate-spin"
      aria-hidden="true"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    startIcon,
    endIcon,
    fullWidth = false,
    disabled,
    className,
    children,
    type = 'button',
    writes,
    ...rest
  },
  ref,
) {
  const { t } = useTranslation();
  const demoLocked = useDemoLock();
  const isWrite = writes ?? (type === 'submit' || variant === 'danger');
  const locked = demoLocked && isWrite;
  const base =
    'rounded-[4px] font-display font-medium inline-flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150';
  const widthClass = fullWidth ? 'w-full' : '';
  const classes = [
    base,
    SIZE_CLASSES[size],
    VARIANT_CLASSES[variant],
    widthClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled || loading || locked}
      title={locked ? t('demo.lockedTitle') : rest.title}
      className={classes}
    >
      {loading ? <Spinner size={SPINNER_SIZE[size]} /> : startIcon}
      {children}
      {!loading && endIcon}
    </button>
  );
});

export default Button;
